import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';

import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';

import { Card } from '@/components/ui/card';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import { LimitAutocomplete } from '@/components/ui/limit-autocomplete';
import { ScenarioAutocomplete } from '@/components/ui/scenario-autocomplete';
import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { PriorityInput, type PriorityItem } from '@/components/ui/priority-input';

import type { LimitPolicyFormValues } from './limit-policy-input';

interface BillingCycleOption {
	billingCycleId: number;
	name: string;
}

interface LimitPolicyFormProps {
	mode: 'create' | 'clone' | 'edit';
	form: UseFormReturn<LimitPolicyFormValues>;
	billingCycles: BillingCycleOption[];
	existingPriorities?: PriorityItem[];
	onSubmit: (values: LimitPolicyFormValues) => Promise<void> | void;
	isSubmitting: boolean;
	initialLimitLabel?: string;
	initialScenarioLabel?: string;
	headerTitle: string;
	onBack?: () => void;
}

export const LimitPolicyForm = ({
	mode,
	form,
	billingCycles,
	existingPriorities = [],
	onSubmit,
	isSubmitting,
	initialLimitLabel,
	initialScenarioLabel,
	headerTitle,
	onBack
}: LimitPolicyFormProps) => {
	const { t } = useTranslation('limitPolicies');
	const nameInputRef = useRef<HTMLInputElement>(null);

	// Auto-focus name field when creating a new limit policy
	useEffect(() => {
		if (mode === 'create') {
			setTimeout(() => {
				nameInputRef.current?.focus();
			}, 0);
		}
	}, [mode]);

	const billingCycleOptions = useMemo(
		() =>
			billingCycles.map(cycle => ({
				value: String(cycle.billingCycleId),
				label: cycle.name
			})),
		[billingCycles]
	);

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				control={form.control}
				rightContent={
					<FormActionButtons
						mode={mode}
						isSubmitting={isSubmitting}
						validationErrors={form.formState.errors}
						formId="limit-policy-form"
					/>
				}
			/>

			<Form {...form}>
			<form id="limit-policy-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<Card className="space-y-6">
					<div className="space-y-1">
						<h2 className="text-lg font-semibold">
							{t('form.generalSettings')}
						</h2>
					</div>

					<div className="grid gap-6 md:grid-cols-2">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.name')}</FormLabel>
									<FormControl>
										<Input {...field} ref={nameInputRef} autoComplete="off" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="priority"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.priority')}</FormLabel>
									<FormControl>
										<PriorityInput
											value={field.value}
											onChange={field.onChange}
											existingItems={existingPriorities}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="scenarioId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.scenario')}</FormLabel>
									<FormControl>
										<ScenarioAutocomplete
											value={field.value || ''}
											onChange={scenarioId =>
												field.onChange(scenarioId)
											}
											placeholder={t(
												'form.scenarioPlaceholder'
											)}
											className="w-full"
											enableCreateNew={true}
											initialSelectedLabel={
												initialScenarioLabel
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</Card>

				<Card className="space-y-6">
					<div className="space-y-1">
						<h2 className="text-lg font-semibold">
							{t('form.limitEnforcement')}
						</h2>
					</div>

					<div className="grid gap-6 md:grid-cols-2">
						<FormField
							control={form.control}
							name="limitId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.limit')}</FormLabel>
									<FormControl>
										<LimitAutocomplete
											value={field.value || ''}
											onChange={limitId =>
												field.onChange(limitId)
											}
											placeholder={t(
												'form.limitPlaceholder'
											)}
											className="w-full"
											enableCreateNew={true}
											initialSelectedLabel={
												initialLimitLabel
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="billingCycleId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.billingCycle')}
									</FormLabel>
									<FormControl>
										<SentryDropdown
											options={billingCycleOptions}
											value={field.value || ''}
											onChange={value =>
												field.onChange(value as string)
											}
											placeholder={t(
												'form.billingCyclePlaceholder'
											)}
											disabled={
												!billingCycleOptions.length
											}
											searchable
											className="w-full"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</Card>
			</form>
		</Form>
		</>
	);
};
